// YNX — AGENDA MODULE (rendez-vous électronique privé)
// Now with weekly/monthly recurrences + 1-click Jitsi video from each appointment.
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Clock, MapPin, X, Video, Repeat } from 'lucide-react';
import {
  listAppointments, createAppointment, deleteAppointment, updateAppointment,
} from '../../lib/biz';
import { createMeeting } from '../../lib/meetings';
import { JitsiRoom } from './JitsiRoom';

const EMPTY_FORM = {
  title: '', start: '', end: '', location: '', description: '', color: '#fbbf24',
  recurrence_rule: 'none', recurrence_count: 1,
};

const RECURRENCE_LABEL = {
  none:    'Aucune',
  daily:   'Quotidien',
  weekly:  'Hebdomadaire',
  monthly: 'Mensuel',
};

export const AgendaModule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setItems(await listAppointments()); }
    catch (e) { console.warn('YNX agenda: refresh failed', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!form.title.trim() || !form.start) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        recurrence_count: Math.max(1, Math.min(52, Number(form.recurrence_count) || 1)),
      };
      if (payload.recurrence_rule === 'none') payload.recurrence_count = 1;
      await createAppointment(payload);
      setForm(EMPTY_FORM);
      setShowForm(false);
      await refresh();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    await deleteAppointment(id);
    await refresh();
  };

  const startVideo = async (apt) => {
    setBusy(true);
    try {
      // Reuse an existing meeting if this RDV already has one (idempotent).
      let meeting;
      if (apt.meeting_id) {
        // The list endpoint already returned us the meeting_id; we still need
        // a full meeting doc to mount Jitsi. Quickest path: create another
        // session with the same room slug — Jitsi-side it's the same room.
        meeting = {
          id: apt.meeting_id,
          title: apt.title || 'Visio',
          room_name: `ynx-apt-${apt.id}`,
          jitsi_host: 'meet.jit.si',
          join_url: `https://meet.jit.si/ynx-apt-${apt.id}`,
        };
      } else {
        meeting = await createMeeting({
          title: apt.title || 'Visio',
          description: apt.description || '',
          scheduled_at: apt.start || '',
          duration_min: 60,
        });
        // Persist meeting_id on the appointment for future 1-click re-open.
        try {
          await updateAppointment(apt.id, { ...apt, meeting_id: meeting.id });
        } catch (err) { console.debug('YNX agenda: persist meeting_id failed', err); }
      }
      setActiveMeeting(meeting);
    } catch (e) {
      alert('Impossible de démarrer la visio : ' + (e.message || ''));
    } finally { setBusy(false); }
  };

  // Group by date
  const groups = items.reduce((acc, it) => {
    const day = (it.start || '').slice(0, 10) || 'sans date';
    (acc[day] = acc[day] || []).push(it);
    return acc;
  }, {});
  const sortedDays = Object.keys(groups).sort();

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>📅 Agenda</h2>
        <button className='biz-btn-primary sm' onClick={() => setShowForm(true)} data-testid='agenda-add-btn'>
          <Plus size={14} /> Nouveau rendez-vous
        </button>
      </header>

      {loading && <div className='biz-loading'>Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className='biz-empty'>Aucun rendez-vous pour le moment. Cliquez sur &quot;Nouveau rendez-vous&quot;.</div>
      )}

      {!loading && sortedDays.map((day) => (
        <section key={day} className='biz-agenda-day'>
          <h3 className='biz-agenda-day-title'>
            {day === 'sans date' ? 'Sans date' : new Date(day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </h3>
          {groups[day].map((apt) => (
            <article
              key={apt.id}
              className='biz-agenda-card'
              style={{ borderLeftColor: apt.color || 'var(--cyan)' }}
              data-testid={`agenda-card-${apt.id}`}
            >
              <div className='biz-agenda-time'>
                <Clock size={12} />
                {apt.start ? new Date(apt.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                {apt.end && ` → ${new Date(apt.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                {apt.recurrence_rule && apt.recurrence_rule !== 'none' && (
                  <span className='biz-agenda-recur' title={`Récurrent · ${RECURRENCE_LABEL[apt.recurrence_rule]}`}>
                    <Repeat size={10} /> {RECURRENCE_LABEL[apt.recurrence_rule]}
                  </span>
                )}
              </div>
              <div className='biz-agenda-body'>
                <h4>{apt.title}</h4>
                {apt.description && <p>{apt.description}</p>}
                {apt.location && <p className='biz-agenda-loc'><MapPin size={11} /> {apt.location}</p>}
              </div>
              <div className='biz-agenda-actions'>
                <button
                  className='biz-btn-primary sm'
                  onClick={() => startVideo(apt)}
                  disabled={busy}
                  data-testid={`agenda-video-${apt.id}`}
                  title='Démarrer une réunion vidéo Jitsi pour ce RDV'
                >
                  <Video size={12} /> Démarrer visio
                </button>
                <button className='biz-icon-btn danger' onClick={() => remove(apt.id)} title='Supprimer'>
                  <Trash2 size={13} />
                </button>
              </div>
            </article>
          ))}
        </section>
      ))}

      {showForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className='biz-modal'>
            <header className='biz-modal-head'>
              <h3>Nouveau rendez-vous</h3>
              <button className='biz-icon-btn' onClick={() => setShowForm(false)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <label className='biz-label'>Titre *</label>
              <input className='biz-input' name='apt-title' value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder='Rencontre client ACME' data-testid='apt-title-input' />

              <label className='biz-label'>Début *</label>
              <input className='biz-input' name='apt-start' type='datetime-local' value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid='apt-start-input' />

              <label className='biz-label'>Fin</label>
              <input className='biz-input' name='apt-end' type='datetime-local' value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />

              <div className='biz-agenda-recur-row'>
                <div>
                  <label className='biz-label'><Repeat size={11} /> Récurrence</label>
                  <select
                    className='biz-input'
                    value={form.recurrence_rule}
                    onChange={(e) => setForm({ ...form, recurrence_rule: e.target.value })}
                    data-testid='apt-recurrence-select'
                  >
                    <option value='none'>Aucune (rendez-vous unique)</option>
                    <option value='daily'>Quotidien</option>
                    <option value='weekly'>Hebdomadaire</option>
                    <option value='monthly'>Mensuel</option>
                  </select>
                </div>
                {form.recurrence_rule !== 'none' && (
                  <div>
                    <label className='biz-label'>Occurrences</label>
                    <input
                      className='biz-input'
                      type='number'
                      min={1}
                      max={52}
                      value={form.recurrence_count}
                      onChange={(e) => setForm({ ...form, recurrence_count: e.target.value })}
                      data-testid='apt-recurrence-count'
                    />
                  </div>
                )}
              </div>

              <label className='biz-label'>Lieu</label>
              <input className='biz-input' name='apt-loc' value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder='Bureau, visio, adresse…' />

              <label className='biz-label'>Description</label>
              <textarea className='biz-input' name='apt-desc' rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

              <label className='biz-label'>Couleur</label>
              <input className='biz-input' name='apt-color' type='color' value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 60, height: 36, padding: 2 }} />
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowForm(false)}>Annuler</button>
              <button className='biz-btn-primary' onClick={add} disabled={busy} data-testid='apt-save-btn'>
                {busy ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {activeMeeting && (
        <JitsiRoom
          meeting={activeMeeting}
          displayName='Hôte YNX'
          onClose={() => { setActiveMeeting(null); refresh(); }}
        />
      )}
    </div>
  );
};
